import { AutoComplete, Button, Checkbox, Col, DatePicker, Form, Input, Row, Space, Tabs, Tooltip, } from "antd";
import { translate } from "../../../core";
import { Combo, ComboOption, CopyToClipboard, NumericInput, } from "../../../core/component";
import { Item, ItemEntry, ItemStone, ItemType } from "../type";
import { ReactNode, useContext, useEffect, useState } from "react";
import { ItemFormJewelery } from "./ItemFormJewelery";
import { ItemFormWatch } from "./ItemFormWatch";
import { ItemFormPatternNumber, ItemFromGiftwareTableware } from "./ItemFromGiftwareTableware";
import { Contact } from "../../contact/type/contact";
import { dateFormat, makeAnObjectEmpty, stringConcat, } from "../../../core/until";
import { ItemFormStone } from "./ItemFormStone";
import { KeyValuePair, PageContextValue, } from "../../../core/type";
import { ItemPageContext } from "./ItemPageContext";
import { ItemFormImages } from "./ItemFormImages";
import { ItemFormQuantity } from "./ItemFormQuantity";
import { ItemFormSales } from "./ItemFormSales";
import { ItemFormComponents } from "./ItemFormComponents";
import { InputCard } from "./InputCard";
import styles from "./ItemForm.module.scss";
import { useItemData } from "../hook/item-data-hook";
import { useLazyQuery } from "@apollo/client";
import { GetVendorsForLookup } from "../../contact/graphql/contact.queries.graphql";
import { ItemFormCategory } from "./ItemFormCategory";
import { ItemFormVideos } from "./ItemFormVideos";
import TabPane from "antd/es/tabs/TabPane";
import { CalculatorOutlined } from "@ant-design/icons";
import { createStoneDescription } from '../func/create-storn-description'

export const ItemForm = () => {
  const { form, save, entity, onFormChange } =
    useContext<PageContextValue<Item, ItemEntry>>(ItemPageContext);
  const [type, setType] = useState<ItemType>();
  const [calculatedMarkup, setCalculatedMarkup] = useState<number>(0);
  const [calculatedOursPrice, setCalculatedOursPrice] = useState<number>(0);
  const [vendorsList, setVendorsList] = useState<Contact[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Contact>();
  const { toEntry } = useItemData();
  const [findVendors] = useLazyQuery(GetVendorsForLookup);

  const typeComponentMapper = {
    [ItemType.Jewelery]: <ItemFormJewelery />,
    [ItemType.Watch]: <ItemFormWatch />,
    [ItemType.Giftware]: <ItemFromGiftwareTableware type="giftware" />,
    [ItemType.Stone]: <ItemFormStone />,
    [ItemType.Tableware]: <ItemFromGiftwareTableware type="tableware" />,
  };

  const patternNumberComponentMapper: Partial<Record<ItemType, ReactNode>> = {
    [ItemType.Giftware]: <ItemFormPatternNumber type="giftware" />,
    [ItemType.Tableware]: <ItemFormPatternNumber type="tableware" />,
  };

  const typeOptions = Object.entries({
    [ItemType.Jewelery]: ItemType.Jewelery,
    [ItemType.Watch]: ItemType.Watch,
    [ItemType.Giftware]: ItemType.Giftware,
    [ItemType.Stone]: ItemType.Stone,
    [ItemType.Tableware]: ItemType.Tableware,
  }).map<ComboOption<KeyValuePair>>(([key, value]) => ({
    value: key,
    display: value,
    item: { key: key.toString(), value },
  }));

  const getMarkup = (): number => {
    const markup = selectedVendor?.markupNumber ?? 0
    return markup === 0 ? 1 : markup
  }

  const handleFieldsChange = (changedFields: any, allFields: any) => {
    onFormChange?.()
  };

  useEffect(() => {
    if (!entity) return;

    const formContent = form?.getFieldsValue();
    form?.setFieldsValue(makeAnObjectEmpty(formContent));
    form?.setFieldsValue(toEntry(entity));
    setType(entity.type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  return (
    <Form
      onFieldsChange={handleFieldsChange}
      layout="vertical"
      name="contentEntry"
      form={form}
      onFinish={save}
    >
      <InputCard contained>
        <Row align="top" gutter={[16, 16]} className="mt-2">
          <Col span={5}>
            <Form.Item
              label={translate("type")}
              name="type"
              rules={[
                {
                  required: true,
                  message: translate("missing_field_message", "type"),
                },
              ]}
            >
              <Combo<any, ItemType>
                size="middle"
                options={typeOptions}
                onSelect={(value) => {
                  setType(value);
                }}
                onBound={({ value }: { value: ItemType }) => setType(value)}
              />
            </Form.Item>
          </Col>

          <Col span={9}>
            <Form.Item
              label={translate("description")}
              shouldUpdate
            >
              {({ getFieldValue }) => {
                let options: { value: string }[] = []
                const type = getFieldValue('type') as ItemType
                if (type === ItemType.Stone) {
                  const currentDescription = getFieldValue('description')
                  const suggestedText = createStoneDescription(getFieldValue('stone') as ItemStone)

                  if (suggestedText.trim() && currentDescription !== suggestedText)
                    options = [{ value: suggestedText }]
                }

                return (<Form.Item
                  noStyle
                  name="description"
                >
                  <AutoComplete
                    options={options}
                  />
                </Form.Item>)
              }}
            </Form.Item>
          </Col>

          <Col span={5}>
            <Form.Item
              label={translate("purchase_date_abbr")}
              name="dateOfPurchase"
            >
              <DatePicker format={dateFormat} style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col span={5} className="d-flex align-items-start">
            <Space direction="vertical" className='gap-0' size={0}>
              <Form.Item name="showOnline" valuePropName="checked" style={{ marginBottom: 4 }}>
                <Checkbox>{translate("show", "on", "web")}</Checkbox>
              </Form.Item>

              <Form.Item name="discontinued" valuePropName="checked" style={{ marginBottom: 4 }}>
                <Checkbox>{translate("discontinued")}</Checkbox>
              </Form.Item>

              <Form.Item name="nonPurchasable" valuePropName="checked" style={{ marginBottom: 4 }}>
                <Checkbox>{translate("non_purchasable")}</Checkbox>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate
              >
                {({ getFieldValue }) => {
                  const nonPurchasable = getFieldValue('nonPurchasable') ?? false
                  if (!nonPurchasable) return null
                  return (
                    <Form.Item
                      name="nonPurchasablePriceEnabled"
                      valuePropName="checked"
                      className='ms-3'
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox>{translate("non_purchasable_suppress_price")}</Checkbox>
                    </Form.Item>
                  )
                }}
              </Form.Item>
            </Space>
          </Col>
        </Row>
      </InputCard>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <InputCard contained className='mt-4 mb-0 justify-content-start'>
          <Row className={styles["customerInfoCard"]}>
            <Col span={24} className="f-xlarge">
              <Space>
                <CopyToClipboard
                  text={entity?.sku ?? ""}
                  tooltip={translate("copy", "sku")}
                />
                {translate("sku")}
              </Space>
            </Col>
            <Col span={24}>
              <Form.Item
                name="sku"
                rules={[
                  {
                    required: true,
                    message: translate("missing_field_message", "sku"),
                  },
                ]}
              >
                <Input disabled={true} style={{ color: "unset" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24} className="f-xlarge">{translate("vendor")}</Col>
            <Col span={24} style={{ width: "250px" }}>
              <Form.Item name="vendorId">
                <Combo<Contact, number>
                  size="middle"
                  onSelect={(_, vendor) => {
                    setSelectedVendor(vendor)
                  }}
                  onBound={(vendors) => {
                    const [vendor] = [vendors].flat()
                    setSelectedVendor(vendor)
                  }}
                  fetch={async ({ request, value, search }) => {
                    const { data } = await findVendors({
                      variables: {
                        request: {
                          take: request?.take ?? 100,
                          skip: request?.skip ?? 0,
                          searchVendor: search,
                          id: value,
                        },
                      },
                    });

                    const list = data?.contactsFind.data ?? [];
                    setVendorsList((prev) => {
                      const newVendors = list.filter(
                        (vendor) => !prev.some((v) => v.id === vendor.id)
                      );
                      return [...prev, ...newVendors];
                    });

                    return list.map((e) => ({
                      value: e.id || 0,
                      display: stringConcat(e.contactId, " - ", e.company),
                      item: e || {},
                    }));
                  }}
                />
              </Form.Item>
              {(type === ItemType.Jewelery && selectedVendor) ? (
                <div className="text-muted f-medium">
                  {translate("markup")}: {selectedVendor.markupNumber || 1}
                </div>
              ) : null
              }
            </Col>
          </Row>

          <Row>
            <Col span={24} className="f-xlarge">{translate("style", "/", "pattern")}</Col>
            <Col span={24}>
              <Form.Item name="style">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </InputCard>

        <InputCard
          contained
          className='mt-4 mb-0 justify-content-start'
          title={translate("dollars")}
        >
          <Space direction="vertical">
            <table className={`${styles["priceTable"]} f-medium`}>
              <tbody>
                <tr>
                  <td></td>
                  <td className="text-center">{translate("price")}</td>
                  <td className="text-center">{translate("sale")}</td>
                </tr>
                <tr>
                  <td className="text-center">
                    <Space className="d-flex align-items-center">
                      {type === ItemType.Jewelery && (
                        <Tooltip title={translate("calculate", "markup")}>
                          <Button
                            type="text"
                            icon={<CalculatorOutlined />}
                            onClick={() => {
                              const constPrice = form?.getFieldValue('costPrice')
                              const markup = getMarkup()
                              form?.setFieldValue('listPrice', markup * constPrice)
                            }}
                          />
                        </Tooltip>
                      )}
                      {translate("cost")}
                    </Space>
                  </td>
                  <td>
                    <Form.Item noStyle name="costPrice">
                      <NumericInput />
                    </Form.Item>
                  </td>
                  <td>
                    <Form.Item noStyle name="costSale">
                      <NumericInput />
                    </Form.Item>
                  </td>
                </tr>
                <tr>
                  <td className="text-center">
                    <Space className="d-flex align-items-center">
                      {type === ItemType.Jewelery && (
                        <Tooltip title={translate("calculate", "our", "price")}>
                          <Button
                            type="text"
                            icon={<CalculatorOutlined />}
                            onClick={() => {
                              const listPrice = form?.getFieldValue('listPrice') ?? 0
                              form?.setFieldValue('oursPrice', listPrice / 2)
                            }}
                          />
                        </Tooltip>
                      )}
                      {translate("list")}
                    </Space>
                  </td>
                  <td>
                    <Form.Item noStyle name="listPrice">
                      <NumericInput
                        className="w-sm"
                      />
                    </Form.Item>
                  </td>
                  <td>
                    <Form.Item noStyle name="listSale">
                      <NumericInput className="w-sm" />
                    </Form.Item>
                  </td>
                </tr>
                <tr>
                  <td className="text-center">{translate("ours")}</td>
                  <td>
                    <Form.Item noStyle name="oursPrice">
                      <NumericInput className="w-sm" />
                    </Form.Item>
                  </td>
                  <td>
                    <Form.Item noStyle name="oursSale">
                      <NumericInput className="w-sm" />
                    </Form.Item>
                  </td>
                </tr>
                <tr>
                  <td className="text-center f-xlarge">{translate("sale")}</td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td className="text-center">{translate("start")}</td>
                  <td>
                    <Form.Item noStyle name="saleStartDate">
                      <DatePicker
                        allowClear
                        suffixIcon={null}
                        format={dateFormat}
                        placeholder={""}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td className="text-center">{translate("end")}</td>
                  <td>
                    <Form.Item noStyle name="saleEndDate">
                      <DatePicker
                        allowClear
                        suffixIcon={null}
                        format={dateFormat}
                        placeholder={""}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </Space>
        </InputCard>

        <ItemFormQuantity />

        <ItemFormSales />

        <InputCard className="mt-4 justify-content-start" contained>
          <Tabs
            defaultActiveKey="1"
            renderTabBar={(props, DefaultTabBar) => (
              <div>
                <DefaultTabBar {...props} />
              </div>
            )}
            className={styles["customerInfoCard"]}
          >
            <TabPane tab={translate("upload_images")} key="1" className="f-xlarge bg-white">
              <Form.Item noStyle name="images">
                <ItemFormImages />
              </Form.Item>
            </TabPane>
            <TabPane tab={translate("upload_videos")} key="2">
              <Form.Item noStyle name="videos">
                <ItemFormVideos />
              </Form.Item>
            </TabPane>
          </Tabs>
        </InputCard>
      </div>

      <InputCard className="my-5 bg-white pt-4" title={translate("category")}>
        <Space align="start" size={[48, 8]} wrap>
          <Form.Item name="category">
            <ItemFormCategory />
          </Form.Item>

          {type && patternNumberComponentMapper[type]}
        </Space>
      </InputCard>

      <Row gutter={[16, 16]} className="my-4 mx-0">
        <InputCard className="bg-white pt-4" title={translate("notes")} noBorder>
          <Form.Item name="notes">
            <Input.TextArea rows={8} />
          </Form.Item>
        </InputCard>

        <Col span={14}>
          <InputCard className="bg-white pt-4" title={translate("catalog", "description")} noBorder>
            <Col span={24}>
              <Form.Item name="catalogDescription">
                <Input.TextArea rows={8} />
              </Form.Item>
            </Col>
          </InputCard>
        </Col>

        <Col
          span={4}
          className="d-flex flex-column justify-content-center align-items-center "
        >
          <Button>{translate("copy")}</Button>
          <span className="mt-1">{translate("to", "item", "number_abbr")}</span>
          <Input className="mt-1" />
        </Col>
      </Row>

      <InputCard className="my-5 bg-white pt-4" title={translate("website", "seo")}>
        <Row gutter={[16, 16]} className="mx-0">
          <Col span={10}>
            <Form.Item label={translate("meta_title")} name="metaTitle">
              <Input />
            </Form.Item>
          </Col>
          <Col span={14}>
            <Form.Item label={translate("meta_description")} name="metaDescription">
              <Input.TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>
      </InputCard>

      {type && typeComponentMapper[type]}

      <ItemFormComponents />
    </Form>
  );
};
